import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Image, Tooltip } from 'antd-v5';
import { get, isNull, startsWith } from 'lodash';
import { MdClose, MdError } from 'react-icons/md';
import { FormattedMessage } from 'react-intl';

import { Button, Spin } from '@shared/components';
import { intl } from '@shared/localization';

import styles from './AdvRequirementsComments.module.scss';

type RequirementCommentImageProps = {
  onDeletePhoto: () => void;
  editable?: boolean;
  photo: object | Blob;
};

function RequirementCommentImage({
  editable,
  onDeletePhoto,
  photo,
}: RequirementCommentImageProps) {
  const previewUrl = useMemo(
    () =>
      photo instanceof Blob && startsWith(photo.type, 'image')
        ? URL.createObjectURL(photo)
        : null,
    [photo],
  );

  useEffect(
    () => () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    },
    [previewUrl],
  );

  const imagePreviewUri = get(photo, 'smallImage', previewUrl);
  const imageUri = get(photo, 'largeImage', previewUrl);
  const pending = get(photo, 'pending', false);
  const failed = get(photo, 'failed', false);
  const hasLocalPreview = startsWith(imagePreviewUri ?? undefined, 'blob:');
  const [previewVisible, setPreviewVisible] = useState(false);
  const [imageError, setImageError] = useState(false);

  useEffect(() => setImageError(false), [imagePreviewUri]);

  const onShowPreview = useCallback(() => setPreviewVisible(true), []);

  const handleDeleteComment = useCallback(
    () => onDeletePhoto(),
    [onDeletePhoto],
  );

  const handleOnError = useCallback(() => setImageError(true), []);

  if ((failed && !pending) || imageError) {
    return (
      <Tooltip
        title={
          <FormattedMessage id="AdvRequirementsComments.imageLoadFailed" />
        }
      >
        <div className={styles.requirementCommentError}>
          <MdError />
        </div>
      </Tooltip>
    );
  }

  if (pending && !hasLocalPreview) {
    return (
      <div className={styles.requirementCommentPending}>
        <Spin />
      </div>
    );
  }

  return (
    <div className={styles.requirementCommentImageContainer}>
      {editable && (
        <Button
          className={styles.requirementCommentImageDelete}
          fitText
          ghost
          noBorder
          onClick={handleDeleteComment}
        >
          <MdClose className={styles.requirementCommentImageDeleteIcon} />
        </Button>
      )}
      {!isNull(imagePreviewUri) && (
        <Button
          className={styles.requirementCommentImageButton}
          fitText
          ghost
          noBorder
          onClick={onShowPreview}
        >
          <img
            alt={intl.formatMessage({
              id: 'AdvRequirementsComments.photoAlt',
            })}
            className={styles.requirementCommentImage}
            loading="lazy"
            onError={handleOnError}
            src={imagePreviewUri}
          />
        </Button>
      )}
      {!isNull(imageUri) && (
        <Image
          src={imageUri}
          style={{ display: 'none' }}
          preview={{
            visible: previewVisible,
            onVisibleChange: setPreviewVisible,
          }}
        />
      )}
    </div>
  );
}

export default RequirementCommentImage;
